import type { RetailerRegionView } from '@vizit/api';
import { isOtherRetailerRegion } from '@vizit/constants';
import { getRetailerIcon } from '@vizit/molecules';
import { useMemo } from 'react';
import type { ClearableSelectItem } from '../clearable-select';
import { ClearableSelect } from '../clearable-select';

interface RetailerSelectProps {
  /**
   * List of retailers to display, fetched from the API.
   * Returns null when fewer than 2 retailers — no point filtering.
   */
  retailers: RetailerRegionView[];
  /**
   * The currently selected retailer region id, or undefined when "All retailers" is selected.
   */
  selectedRetailer?: string;
  /**
   * Callback fired when selection changes. Receives undefined when "All" is chosen.
   */
  onRetailerChange: (retailer: string | undefined) => void;
  /**
   * Whether the Other retailer is shown, below a divider, with a US flag icon
   *
   * @optional
   * @default false
   */
  showOther?: boolean;
  /** Optional additional class name */
  className?: string;
}

function RetailerSelect(props: RetailerSelectProps) {
  const { retailers, selectedRetailer, onRetailerChange, showOther = false, className, ...other } = props;

  const items = useMemo<ClearableSelectItem[]>(
    () =>
      retailers.map(({ id, display_name }, index) => {
        const IconComponent = getRetailerIcon(id, { showOther });
        const isOther = showOther && isOtherRetailerRegion(id);
        return {
          id,
          label: display_name,
          icon: IconComponent ? <IconComponent size={14} /> : undefined,
          hasSeparatorBefore: isOther && index > 0,
        };
      }),
    [retailers, showOther],
  );

  if (retailers.length <= 1) return null;

  // A saved retailer that is not listed (e.g. Other while hidden) reads as no selection.
  const selectedKey = items.some(({ id }) => id === selectedRetailer) ? (selectedRetailer ?? null) : null;

  return (
    <ClearableSelect
      ariaLabel="Retailer"
      placeholder="All retailers"
      items={items}
      selectedKey={selectedKey}
      onSelectionChange={(key) => onRetailerChange(key ?? undefined)}
      className={className}
      {...other}
    />
  );
}

export { RetailerSelect };
export type { RetailerSelectProps };
